/**
 * Application errors.
 *
 * Route handlers throw these; one error middleware in `apps/api` maps them to status codes and
 * response bodies. The rule that matters: a Prisma error, a stack trace, or a raw database message
 * must never reach a client — the middleware logs the cause and returns the mapped shape.
 */

export type ErrorCode =
  | 'BAD_REQUEST'
  | 'VALIDATION_FAILED'
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'PASSWORD_CHANGE_REQUIRED'
  | 'SALARY_PIN_REQUIRED'
  | 'SALARY_PIN_INCORRECT'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'PRECONDITION_FAILED'
  | 'RATE_LIMITED'
  | 'PAYLOAD_TOO_LARGE'
  | 'UNPROCESSABLE'
  | 'GONE'
  | 'AI_ALLOWANCE_EXHAUSTED'
  | 'AI_UNAVAILABLE'
  | 'PLAN_LIMIT_REACHED'
  | 'SERVICE_UNAVAILABLE'
  | 'INTERNAL';

export interface ErrorBody {
  readonly code: ErrorCode;
  readonly message: string;
  readonly details?: unknown;
  readonly requestId?: string;
}

export class AppError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details?: unknown;
  /**
   * Whether the message is safe to show a user. Internal errors carry an operator-facing message
   * that the middleware replaces with something generic.
   */
  readonly expose: boolean;

  constructor(
    code: ErrorCode,
    status: number,
    message: string,
    options?: { details?: unknown; expose?: boolean; cause?: unknown },
  ) {
    super(message, options?.cause !== undefined ? { cause: options.cause } : undefined);
    this.name = new.target.name;
    this.code = code;
    this.status = status;
    this.details = options?.details;
    this.expose = options?.expose ?? status < 500;
    // V8-only (Node, Chrome): trims the constructor frames off the stack. Typed locally rather
    // than via @types/node because this package is RN-safe and consumed from browser tsconfigs
    // that deliberately do not load Node types — the `?.` already makes it a no-op elsewhere.
    (
      Error as ErrorConstructor & { captureStackTrace?: (target: object, ctor?: unknown) => void }
    ).captureStackTrace?.(this, new.target);
  }

  toBody(requestId?: string): ErrorBody {
    return {
      code: this.code,
      message: this.expose ? this.message : 'An unexpected error occurred.',
      ...(this.details !== undefined && this.expose ? { details: this.details } : {}),
      ...(requestId ? { requestId } : {}),
    };
  }
}

export class BadRequestError extends AppError {
  constructor(message = 'Bad request', details?: unknown) {
    super('BAD_REQUEST', 400, message, { details });
  }
}

export class ValidationError extends AppError {
  constructor(message = 'Validation failed', details?: unknown) {
    super('VALIDATION_FAILED', 422, message, { details });
  }
}

export class UnauthenticatedError extends AppError {
  constructor(message = 'Authentication required', details?: unknown) {
    super('UNAUTHENTICATED', 401, message, { details });
  }
}

/**
 * Carries the permission that was missing so the audit trail records *what* was refused, not just
 * that something was. The message sent to the client stays generic.
 */
export class ForbiddenError extends AppError {
  readonly permission?: string;

  constructor(message = 'You do not have permission to do that', permission?: string) {
    super('FORBIDDEN', 403, message);
    this.permission = permission;
  }
}

/**
 * The signed-in person holds an HR-issued temporary password (`credential.mustChangePassword`) and
 * may call nothing but the change-password flow until they replace it. A 403 with this stable
 * machine code (not the generic FORBIDDEN) so clients can tell "you may never do this" from "finish
 * setting your password first" and route to the blocking change-password step.
 */
export class PasswordChangeRequiredError extends AppError {
  constructor() {
    super('PASSWORD_CHANGE_REQUIRED', 403, 'You must change your temporary password before continuing');
  }
}

/** Why a pay-bearing response was refused — the client picks the PIN dialog's step from it. */
export type SalaryPinRequiredReason = 'not_set' | 'required' | 'locked' | 'impersonation';

export interface SalaryPinRequiredDetails {
  readonly reason: SalaryPinRequiredReason;
  /** ISO instant, for `locked` only. */
  readonly lockedUntil?: string;
}

const SALARY_PIN_REQUIRED_MESSAGES: Readonly<Record<SalaryPinRequiredReason, string>> = {
  not_set: 'Create a salary PIN to view pay details',
  required: 'Enter your salary PIN to view pay details',
  locked: 'Too many wrong PINs. Try again in a few minutes',
  impersonation: 'Pay details stay locked in a support session',
};

const SALARY_PIN_REQUIRED_REASONS = new Set<string>(Object.keys(SALARY_PIN_REQUIRED_MESSAGES));

/**
 * The response carries other people's pay (ADR 0041) and this login session has not entered the salary PIN in the
 * last five minutes. 403 with a stable code, like PASSWORD_CHANGE_REQUIRED, so clients open the PIN prompt and
 * retry instead of showing "no access".
 */
export class SalaryPinRequiredError extends AppError {
  readonly reason: SalaryPinRequiredReason;

  constructor(reason: SalaryPinRequiredReason, lockedUntil?: Date) {
    const details: SalaryPinRequiredDetails = {
      reason,
      ...(lockedUntil ? { lockedUntil: lockedUntil.toISOString() } : {}),
    };
    super('SALARY_PIN_REQUIRED', 403, SALARY_PIN_REQUIRED_MESSAGES[reason], { details });
    this.reason = reason;
  }
}

/** A wrong PIN. 422, so the API client's PIN-required retry never fires on it. */
export class SalaryPinIncorrectError extends AppError {
  readonly attemptsRemaining: number;

  constructor(attemptsRemaining: number) {
    super('SALARY_PIN_INCORRECT', 422, 'That PIN is not right', { details: { attemptsRemaining } });
    this.attemptsRemaining = attemptsRemaining;
  }
}

/** True for a `SalaryPinRequiredError` body as it arrives over the wire — the client's cue to ask for the PIN. */
export function isSalaryPinRequiredBody(
  body: unknown,
): body is ErrorBody & { readonly details: SalaryPinRequiredDetails } {
  if (typeof body !== 'object' || body === null) return false;
  const b = body as { code?: unknown; details?: unknown };
  if (b.code !== 'SALARY_PIN_REQUIRED' || typeof b.details !== 'object' || b.details === null) return false;
  const reason = (b.details as { reason?: unknown }).reason;
  return typeof reason === 'string' && SALARY_PIN_REQUIRED_REASONS.has(reason);
}

/**
 * Deliberately indistinguishable from a scope miss.
 *
 * When an actor may not see a record, the API returns 404 rather than 403 — otherwise the error
 * code itself confirms the record exists, which leaks org structure and headcount to anyone who
 * can enumerate ids.
 */
export class NotFoundError extends AppError {
  constructor(resource = 'Resource') {
    super('NOT_FOUND', 404, `${resource} not found`);
  }
}

export class ConflictError extends AppError {
  constructor(message = 'Conflicts with the current state', details?: unknown) {
    super('CONFLICT', 409, message, { details });
  }
}

/** For state-machine violations: approving a locked payroll run, editing a closed period. */
export class PreconditionFailedError extends AppError {
  constructor(message = 'Precondition failed', details?: unknown) {
    super('PRECONDITION_FAILED', 412, message, { details });
  }
}

export class RateLimitedError extends AppError {
  readonly retryAfterSeconds?: number;

  constructor(message = 'Too many requests', retryAfterSeconds?: number) {
    super('RATE_LIMITED', 429, message);
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * The body is bigger than the route accepts. Distinct from `ValidationError` because the fix is
 * different in kind: nothing about the file is malformed, there is simply too much of it, and the
 * message should say the limit rather than list a field.
 */
export class PayloadTooLargeError extends AppError {
  constructor(message = 'That file is too large') {
    super('PAYLOAD_TOO_LARGE', 413, message, { expose: true });
  }
}

/**
 * A dependency this route needs is not answering — a missing binary, an unreachable sidecar. A
 * 503, not a 500, and the message IS shown: it tells the person to try again later rather than
 * sending them off to fix an upload that was never the problem. Never quotes the request.
 */
export class ServiceUnavailableError extends AppError {
  constructor(message = 'This service is temporarily unavailable') {
    super('SERVICE_UNAVAILABLE', 503, message, { expose: true });
  }
}

/** The thing existed and no longer does — an expired proposal, a consumed one-time link. */
export class GoneError extends AppError {
  constructor(message = 'This is no longer available') {
    super('GONE', 410, message);
  }
}

/**
 * The org's AI allowance for the period is spent. A 429 like `RateLimitedError`, but with its own
 * code so the UI can say "ask an admin to add more" instead of "slow down".
 */
export class AiAllowanceExhaustedError extends AppError {
  readonly periodEnd?: string;

  constructor(message: string, periodEnd?: string) {
    super('AI_ALLOWANCE_EXHAUSTED', 429, message, {
      details: periodEnd ? { periodEnd } : undefined,
    });
    this.periodEnd = periodEnd;
  }
}

/**
 * No model provider is configured, or the configured one refused (out of credit, bad key).
 * Degrades, never crashes (503, not 500) — and the message is shown, unlike other 5xx: it names
 * the thing an admin has to fix, and never quotes the request.
 */
export class AiUnavailableError extends AppError {
  constructor(message = 'The AI copilot is not configured on this deployment') {
    super('AI_UNAVAILABLE', 503, message, { expose: true });
  }
}

/**
 * The org's plan does not allow another of something — today, a free-trial org adding a sixth
 * employee. A 402, not a 403: the caller is allowed to do this, the plan is not, and the fix is an
 * upgrade rather than a role. `limit` and `used` ride in `details` so the UI can say how many.
 */
export class PlanLimitError extends AppError {
  readonly limit: number;
  readonly used: number;

  constructor({ limit, used }: { limit: number; used: number }, message?: string) {
    super(
      'PLAN_LIMIT_REACHED',
      402,
      message ??
        `Your free trial includes up to ${limit} employees. Upgrade your plan to add more.`,
      { details: { limit, used } },
    );
    this.limit = limit;
    this.used = used;
  }
}

export class InternalError extends AppError {
  constructor(message = 'Internal error', cause?: unknown) {
    super('INTERNAL', 500, message, { expose: false, cause });
  }
}

export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}
